import { UserPlus, Power, Eye, Trash2, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { dateShort } from "@/lib/format";

const FILTERS = [["all", "All"], ["active", "Active"], ["inactive", "Deactivated"]];

export const CarrierFilters = ({ q, setQ, statusF, setStatusF, shown, total }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
    <div className="relative flex-1 max-w-md">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search carrier or admin email…" className="pl-9 pr-9 bg-slate-900 border-slate-800 text-white placeholder:text-slate-500" data-testid="owner-search-input" />
      {q && <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white" aria-label="Clear search" data-testid="owner-search-clear"><X className="w-4 h-4" /></button>}
    </div>
    <div className="flex gap-2">
      {FILTERS.map(([k, label]) => (
        <button key={k} onClick={() => setStatusF(k)} data-testid={`owner-filter-${k}`}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${statusF === k ? "bg-amber-400 text-slate-950 border-amber-400" : "bg-slate-900 text-slate-300 border-slate-800 hover:bg-slate-800"}`}>{label}</button>
      ))}
    </div>
    <span className="text-xs text-slate-500 sm:ml-auto" data-testid="owner-shown-count">{shown} of {total} carriers</span>
  </div>
);

export const ago = (iso) => {
  if (!iso) return "Never";
  const m = Math.round((Date.now() - new Date(iso)) / 60000);
  return m < 60 ? `${Math.max(m, 0)} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;
};

export const StatusPill = ({ r, testId }) => (
  <span className={`pill border ${r.status === "active" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-rose-500/10 text-rose-300 border-rose-500/30"}`} data-testid={testId}>
    {r.status === "active" ? "Active" : "Deactivated"}
  </span>
);

// p = test-id prefix so desktop and mobile copies stay unique
export const CarrierActions = ({ r, on, p = "carrier", className = "" }) => (
  <div className={`flex items-center gap-1 ${className}`}>
    {!r.is_mine && <Button size="sm" variant="ghost" className="text-amber-300 hover:text-amber-200 hover:bg-amber-500/10" onClick={() => on.view(r)} data-testid={`${p}-view-${r.id}`}><Eye className="w-4 h-4 mr-1" /> View data</Button>}
    <Button size="sm" variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => on.admin(r)} data-testid={`${p}-add-admin-${r.id}`}><UserPlus className="w-4 h-4 mr-1" /> Admin</Button>
    {!r.is_mine && <Button size="sm" variant="ghost" className={r.status === "active" ? "text-rose-300 hover:bg-rose-500/10" : "text-emerald-300 hover:bg-emerald-500/10"} onClick={() => on.toggle(r)} data-testid={`${p}-toggle-${r.id}`}><Power className="w-4 h-4 mr-1" /> {r.status === "active" ? "Deactivate" : "Reactivate"}</Button>}
    {!r.is_mine && !r.is_default && <Button size="sm" variant="ghost" className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 ml-auto" onClick={() => on.del(r)} aria-label="Delete carrier" title="Delete carrier" data-testid={`${p}-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>}
  </div>
);

const Num = ({ label, value }) => (
  <div><div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div><div className="text-lg font-semibold text-white tabular-nums">{value}</div></div>
);

export const CarrierCard = ({ r, on }) => (
  <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4 space-y-3" data-testid={`m-carrier-card-${r.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="font-semibold text-white truncate">{r.name}{r.is_mine && <span className="ml-2 text-[10px] uppercase text-amber-300">yours</span>}</div>
        <div className="text-xs text-slate-400 mt-0.5">Last active {ago(r.last_active_at)} · Created {dateShort(r.created_at)}</div>
      </div>
      <StatusPill r={r} testId={`m-carrier-status-${r.id}`} />
    </div>
    <div className="grid grid-cols-3 gap-2"><Num label="Loads" value={r.loads} /><Num label="Drivers" value={r.drivers} /><Num label="Invoices" value={r.invoices} /></div>
    {r.admins.length > 0 && (
      <div className="text-sm text-slate-300 space-y-0.5">
        {r.admins.map((a) => <div key={a.user_id} className="truncate">{a.name || a.email} <span className="text-slate-500">{a.email}</span></div>)}
      </div>
    )}
    <CarrierActions r={r} on={on} p="m-carrier" className="flex-wrap border-t border-slate-800 pt-2 -mx-2" />
  </div>
);
